/**
 * ErgoBot table runtime: the runtime contract, version 1.8, as running code.
 *
 * It chooses the finger numbers a phrase is shown with, says how much each
 * number is needed and which numbers a display level shows, and says whether a
 * phrase can follow the previous one from where it left the hand. Where the next phrase is known it chooses the numbers
 * for both. Everything is worked out on what the written notes sound as in the
 * key in force.
 *
 * Everything here reads the compiled table bundle and nothing else. Nothing is a
 * rule about music: every cost, threshold, finger map and scale fingering is a
 * number in the tables. All arithmetic is on integers.
 *
 * This folder imports nothing from outside itself, so it can be copied whole
 * into another code base.
 */
import type {
  Arrival,
  ColourTerms,
  DisplayLevel,
  Figure,
  Finger,
  Hand,
  HandDisplay,
  HandInput,
  HandResult,
  HandState,
  HandTable,
  Join,
  JoinMode,
  Key,
  Line,
  LineTerms,
  Motion,
  MotionHandTable,
  NeedConstants,
  PositionHandTable,
  Side,
  Tables,
} from './types';

/** The contract version this runtime implements. */
export const CONTRACT_VERSION = '1.8';

const TABLES_FORMAT = 'ergobot-tables';
const FINGERS: ReadonlyArray<Finger> = [1, 2, 3, 4, 5];
const FINGER_COUNT = 5;
const FINGER_PAIRS = FINGER_COUNT * FINGER_COUNT;
const HANDS: ReadonlyArray<Hand> = ['rh', 'lh'];
const MOTIONS: ReadonlyArray<Motion> = [
  'none',
  'stretch',
  'squeeze',
  'pivot',
  'substitution',
  'hop',
  'leap',
];
const BLACK_PITCH_CLASSES: ReadonlyArray<number> = [1, 3, 6, 8, 10];
const LETTERS = 'CDEFGAB';
const NO_ALTERATION: ReadonlyArray<number> = [0, 0, 0, 0, 0, 0, 0];
const OCTAVE_PATTERN = /^-?[0-9]+$/;
const NOT_OF_AN_OCTAVE = /[^0-9-]/g;
const UNREACHABLE = Number.POSITIVE_INFINITY;
const NOT_ALLOWED = -1;
const NOT_REACHED = -1;
const TOP_SCORE = 100;
const WHITE = 0;
const BLACK = 1;
const RESTING_SHAPE = 0;
const ANY_FINGER = 0;
const NO_FINGER = 0;
const NO_COST: ReadonlyArray<number> = [0, 0, 0, 0, 0];
const THUMB_COLUMN = 0;
const FIFTH_COLUMN = FINGER_COUNT - 1;
const NEVER_COMPATIBLE = 'x';
const PITCH_RULE = 'n';
const NOT_FEATURED: ReadonlyArray<number> = [];
/** The motions after which the hand holds two fingers apart from where it has
 *  them at rest. */
const HOLDS_TWO_FINGERS: ReadonlyArray<Arrival> = ['stretch', 'squeeze'];

/** A finger and the shape the hand is in, as one number: 5 * shape + (finger - 1). */
type Seat = number;

/** The seats a note can be reached in, as parallel arrays: `costs[place]` is
 *  the lowest cost `seats[place]` is reached with. */
interface Layer {
  seats: ReadonlyArray<Seat>;
  costs: ReadonlyArray<number>;
}

/** Every way on from the seats of one note to the next note, as parallel
 *  arrays: `targets[move]` is the place in the later note's layer of the seat a
 *  move lands in, `costs[move]` what it costs. The moves that leave the seat at
 *  `place` of the earlier note's layer are those from `starts[place]` up to,
 *  not including, `starts[place + 1]`. */
interface Moves {
  starts: ReadonlyArray<number>;
  targets: ReadonlyArray<number>;
  costs: ReadonlyArray<number>;
}

/** Where a linked move stands: joining two phrases, inside the line, or inside
 *  the line across a rest, the hand held as it is. */
type Where = 'joining' | 'within' | 'across-rest';

/** The moves between two keys before the shape of the hand comes in: per
 *  finger pair, at 5 * (finger1 - 1) + (finger2 - 1), what the move costs
 *  (NOT_ALLOWED when it is not one) and, for linked moves, its change of shape.
 *  `ops` is null when the hand is placed again: it then lands in the seat
 *  `placed` has for the finger it is placed with, at `finger - 1`. */
interface Step {
  costs: ReadonlyArray<number>;
  ops: ReadonlyArray<number> | null;
  placed: ReadonlyArray<Seat>;
}

/** A key with its alterations spelled out. */
interface FullKey {
  transposition: number;
  alter: ReadonlyArray<number>;
}

/** The keys of a hand table as they sound in a key. */
interface Keyboard {
  midi: ReadonlyArray<number>;
  /** 0 white, 1 black. */
  colours: ReadonlyArray<number>;
  /** Which of a motion hand's cost planes applies. */
  keyClass: number;
  /** The finger each pitch is given in a scalar run in this key, if the hand
   *  has a scale for it. */
  scale: ReadonlyArray<number> | null;
  /** Per pitch, what each finger costs on it in this key, if the hand has
   *  costs for it. */
  onKey: ReadonlyArray<ReadonlyArray<number>> | null;
}

/** The span of a line on a hand that has terms on it (contract 5.1, step 6),
 *  in keys counted towards the fifth finger: `towardsFifth` is 1 for a right
 *  hand and -1 for a left, and the two ends are the line's lowest and highest
 *  staff step times that. */
interface Span {
  terms: LineTerms;
  towardsFifth: number;
  /** The line's end on the thumb's side. */
  thumbEnd: number;
  /** The line's end on the fifth finger's side. */
  fifthEnd: number;
}

/** A note before the standard patterns are looked for. */
interface PlainNote {
  /** Of the pitch in the hand table. */
  index: number;
  name: string;
  staffStep: number;
  colour: number;
  afterRest: boolean;
  /** What each finger costs on the note's key in this key; null: nothing. */
  onKey: ReadonlyArray<number> | null;
  /** The span of the line the note is in; null on a hand without terms on it. */
  span: Span | null;
  /** What each finger costs for where it puts the hand against that span;
   *  null: nothing. */
  beyond: ReadonlyArray<number> | null;
}

interface Note extends PlainNote {
  /** The finger a standard pattern gives the note; ANY_FINGER when none does. */
  pattern: number;
}

/** How the first note is entered: with nothing known, or from a hand state on
 *  the pitch `held` of the table. */
type Entry =
  | { mode: 'placed' }
  | { mode: 'rest' | 'linked' | 'replaced'; held: number; state: HandState };

/** A search of a line: per note the layer of seats it is reached in (`forward`)
 *  and, for each seat of that layer at the same place, the cheapest way on to
 *  the end of the line (`backward`, UNREACHABLE where there is none); the moves
 *  from each note to the next; and the cost of the cheapest fingering,
 *  UNREACHABLE when the line has none. */
interface Search {
  entry: Entry;
  forward: ReadonlyArray<Layer>;
  backward: ReadonlyArray<ReadonlyArray<number>>;
  moves: ReadonlyArray<Moves>;
  best: number;
}

/** A search of a line as far as its last note: the layers and the moves, before
 *  anything is known about how the line may end. */
interface Reached {
  entry: Entry;
  forward: ReadonlyArray<Layer>;
  moves: ReadonlyArray<Moves>;
}

/** A fingering being followed through the search: its fingers so far, the shape
 *  the hand is in on each of those notes, and the shape of the seat it came
 *  from into the last of them. */
interface Trail {
  fingers: ReadonlyArray<Finger>;
  shapes: ReadonlyArray<number>;
  cameFrom: number;
}

const AS_WRITTEN: FullKey = { transposition: 0, alter: NO_ALTERATION };
const FREE_JOIN: Join = { compatible: true, hops: 0 };
const NO_JOIN: Join = { compatible: false, hops: 0 };
const NOWHERE_PLACED: ReadonlyArray<Seat> = [];

const notOk = (): HandResult => ({
  ok: false,
  fingers: [],
  need: [],
  arrivals: [],
  gaps: [],
  cost: 0,
  join: 'placed',
  state: null,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const ownValue = <Value>(
  record: Readonly<Record<string, Value>>,
  key: string,
): Value | undefined =>
  Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;

/** The running results of folding `items` from `initial`, `initial` included. */
const accumulate = <Item, Result>(
  items: ReadonlyArray<Item>,
  fold: (result: Result, item: Item) => Result,
  initial: Result,
): Result[] => {
  const results: Result[] = [initial];
  items.forEach((item) => results.push(fold(results[results.length - 1], item)));
  return results;
};

const seatOf = (finger: Finger, shape: number): Seat => FINGER_COUNT * shape + (finger - 1);
const fingerOf = (seat: Seat): Finger => ((seat % FINGER_COUNT) + 1) as Finger;
const shapeOf = (seat: Seat): number => Math.floor(seat / FINGER_COUNT);

// ---------------------------------------------------------------------------
// The bundle (contract, section 2)
// ---------------------------------------------------------------------------

/** The table bundle, refused unless it is one this runtime implements. */
export function checkedTables(bundle: unknown): Tables {
  if (!isRecord(bundle) || bundle.format !== TABLES_FORMAT) {
    throw new Error('not an ErgoBot table bundle');
  }
  if (bundle.contract_version !== CONTRACT_VERSION) {
    throw new Error(
      `contract version ${String(bundle.contract_version)} is not ${CONTRACT_VERSION}`,
    );
  }
  return bundle as unknown as Tables;
}

/** The table of one hand of a tier, or null when the tier does not use that hand. */
export function handTable(tables: Tables, tier: string, hand: Hand): HandTable | null {
  if (HANDS.indexOf(hand) < 0) {
    throw new RangeError(`hand must be one of ${HANDS.join(', ')}, not ${String(hand)}`);
  }
  const tableId = ownValue(tables.tiers, tier)?.[hand];
  return tableId === undefined || tableId === null
    ? null
    : (ownValue(tables.hands, tableId) ?? null);
}

/** What a tier says about the finger numbers of one of its hands (contract
 *  2.9), or null: a tier that is not in the tables, or a hand the tier has no
 *  entry for. */
export function handDisplay(tables: Tables, tier: string, hand: Hand): HandDisplay | null {
  if (HANDS.indexOf(hand) < 0) {
    throw new RangeError(`hand must be one of ${HANDS.join(', ')}, not ${String(hand)}`);
  }
  return ownValue(tables.tiers, tier)?.display?.[hand] ?? null;
}

/** Whether the tier displays the hand's finger numbers at all. Where it does
 *  not, no level shows any of them, whatever their need scores. */
export function showsNumbers(tables: Tables, tier: string, hand: Hand): boolean {
  return handDisplay(tables, tier, hand)?.shown === true;
}

// ---------------------------------------------------------------------------
// Pitches, keys and positions (contract, sections 1.1, 1.3 and 1.4)
// ---------------------------------------------------------------------------

const letterIndex = (name: string): number => LETTERS.indexOf(String(name).charAt(0).toUpperCase());

/** 7 * octave + the index of the letter in CDEFGAB (C4 = 28); accidentals are ignored. */
export function staffStep(name: string): number {
  const octave = String(name).slice(1).replace(NOT_OF_AN_OCTAVE, '');
  if (String(name) === '' || letterIndex(name) < 0 || !OCTAVE_PATTERN.test(octave)) {
    throw new RangeError(`not a pitch name: ${String(name)}`);
  }
  return 7 * parseInt(octave, 10) + letterIndex(name);
}

const thumbStep = (side: Side, step: number, finger: Finger): number =>
  side === 'right' ? step - (finger - 1) : step + (finger - 1);

/** The staff step under the thumb when the hand rests with `finger` on the key. */
export function position(side: Side, name: string, finger: Finger): number {
  return thumbStep(side, staffStep(name), finger);
}

/** 1 for a black key, 0 for a white one. */
export function colourOf(midi: number): number {
  return BLACK_PITCH_CLASSES.indexOf(((midi % 12) + 12) % 12) >= 0 ? BLACK : WHITE;
}

function checkedLine(line: Line): Line {
  if (!line.every((token) => token === null || typeof token === 'string')) {
    throw new TypeError('a line holds pitch names and null for rests');
  }
  return line;
}

function checkedKey(key: Key | null | undefined): FullKey {
  if (key === undefined || key === null) return AS_WRITTEN;
  if (!isRecord(key)) throw new TypeError('a key is a Key');
  const alter = key.alter ?? NO_ALTERATION;
  const whole =
    Number.isInteger(key.transposition) &&
    Array.isArray(alter) &&
    alter.length === LETTERS.length &&
    alter.every((semitones) => Number.isInteger(semitones));
  if (!whole) {
    throw new RangeError('a key has a whole transposition and one whole alteration per letter');
  }
  return { transposition: key.transposition, alter };
}

function checkedState(state: HandState | null | undefined): HandState | null {
  if (state === undefined || state === null) return null;
  if (!isRecord(state)) throw new TypeError('a hand state is a HandState or null');
  if (FINGERS.indexOf(state.finger) < 0) {
    throw new RangeError(`not a finger: ${String(state.finger)}`);
  }
  const shape = state.shape ?? RESTING_SHAPE;
  if (!Number.isInteger(state.midi) || !Number.isInteger(shape) || shape < 0) {
    throw new RangeError('a hand state has a MIDI number and a shape number');
  }
  return state;
}

/** The colours of a hand's keys as one number: bit `p` is set when pitch `p` is black. */
const colourPattern = (colours: ReadonlyArray<number>): number =>
  colours.reduce((pattern, colour, index) => pattern + colour * 2 ** index, 0);

/** The hand's keys as they sound in a key (contract 5.1, step 1). */
function keyboardOf(table: HandTable, key: FullKey): Keyboard {
  const midi = table.midi.map(
    (written, index) => written + key.transposition + key.alter[letterIndex(table.pitches[index])],
  );
  const colours = midi.map(colourOf);
  const colouring = colourPattern(colours);
  const scale =
    table.kind === 'motion' ? ownValue(table.scales ?? {}, String(colouring)) : undefined;
  const onKey =
    table.kind === 'motion' ? ownValue(table.on_key ?? {}, String(colouring)) : undefined;
  return {
    midi,
    colours,
    keyClass: table.kind === 'motion' ? table.key_class[colouring] : 0,
    scale: scale ?? null,
    onKey: onKey ?? null,
  };
}

/** Per note, how many notes the stretch around it holds in which every note is
 *  one staff step in `direction` from the note before. `steps[i]` is the staff
 *  steps from note `i` to note `i + 1` (0 across a rest). */
const runLengths = (steps: ReadonlyArray<number>, direction: number): number[] => {
  const counted = (moves: ReadonlyArray<number>): number[] =>
    accumulate(moves, (run: number, move) => (move === direction ? run + 1 : 0), 0);
  const behind = counted(steps);
  const ahead = counted(steps.slice().reverse()).reverse();
  return behind.map((run, i) => 1 + run + ahead[i]);
};

/** Per note, whether it is part of a scalar run (contract 5.1, step 5):
 *  `pattern.run_notes` or more notes in a row, each a staff step from the one
 *  before, all up or all down, no rest between them. */
const inScalarRun = (tables: Tables, notes: ReadonlyArray<PlainNote>): boolean[] => {
  const steps = notes
    .slice(1)
    .map((note, i) => (note.afterRest ? 0 : note.staffStep - notes[i].staffStep));
  const up = runLengths(steps, 1);
  const down = runLengths(steps, -1);
  return notes.map((_note, i) => Math.max(up[i], down[i]) >= tables.pattern.run_notes);
};

/** Whether a line is the figure (contract 2.6): it plays every key of the
 *  figure and no other and, where the figure is in order, no note is further
 *  than one place in the figure's keys from the note before. */
const isFigure = (figure: Figure, notes: ReadonlyArray<PlainNote>): boolean => {
  const places = notes.map((note) => figure.keys.indexOf(note.index));
  const playsItsKeysOnly =
    places.every((place) => place >= 0) &&
    figure.keys.every((_key, place) => places.indexOf(place) >= 0);
  return (
    playsItsKeysOnly &&
    (!figure.in_order || places.slice(1).every((place, i) => Math.abs(place - places[i]) <= 1))
  );
};

/** Per note, whether it is in a run of a figure that is found inside a line
 *  (contract 2.6): a longest stretch with no rest in it that goes through the
 *  figure's keys from one to the next, and plays every one of them. */
const inRunOf = (figure: Figure, notes: ReadonlyArray<PlainNote>): boolean[] => {
  const places = notes.map((note) => figure.keys.indexOf(note.index));
  const goesOn = (i: number): boolean =>
    !notes[i].afterRest &&
    places[i] >= 0 &&
    places[i - 1] >= 0 &&
    Math.abs(places[i] - places[i - 1]) === 1;
  const firsts = notes.map((_note, i) => i).filter((i) => i === 0 || !goesOn(i));
  const runs = firsts
    .map((first, order): [number, number] => [first, firsts[order + 1] ?? notes.length])
    .filter(([first, last]) => {
      const played = places.slice(first, last);
      return (
        played.every((place) => place >= 0) &&
        figure.keys.every((_key, place) => played.indexOf(place) >= 0)
      );
    });
  return notes.map((_note, i) => runs.some(([first, last]) => first <= i && i < last));
};

/** Per note, the finger of the first figure found inside a line that the note
 *  is in a run of; ANY_FINGER where there is none. */
const insideFingers = (
  figures: ReadonlyArray<Figure>,
  notes: ReadonlyArray<PlainNote>,
): number[] => {
  const found = figures.filter((figure) => figure.inside === true);
  const runs = found.map((figure) => inRunOf(figure, notes));
  return notes.map((note, i) => {
    const first = found.findIndex((_figure, which) => runs[which][i]);
    return first < 0 ? ANY_FINGER : found[first].fingers[note.index];
  });
};

/** Per note, the finger a standard pattern gives it (contract 5.1, step 5),
 *  ANY_FINGER where none does: in a scalar run the finger of the scale the hand
 *  has in this key, else the finger of a figure the note is in a run of, else
 *  the finger of the first figure the whole line is. Where `pattern.whole_line`
 *  is set, a run takes the scale only in a line that is nothing but scalar
 *  runs. A line that is `joined` (it plays on from the phrase before with no
 *  rest) is no figure unless `pattern.joined` is set. */
const patternsOf = (
  tables: Tables,
  table: HandTable,
  keyboard: Keyboard,
  notes: ReadonlyArray<PlainNote>,
  joined: boolean,
): number[] => {
  const figures = table.kind === 'motion' ? (table.figures ?? []) : [];
  const asked = tables.pattern.joined || !joined;
  const figure = asked
    ? figures.find((candidate) => candidate.inside !== true && isFigure(candidate, notes))
    : undefined;
  const inside = insideFingers(figures, notes);
  const scale = keyboard.scale;
  const inRun = scale === null ? notes.map(() => false) : inScalarRun(tables, notes);
  const takesScale = !tables.pattern.whole_line || inRun.every((isInRun) => isInRun);
  return notes.map((note, i) => {
    if (scale !== null && inRun[i] && takesScale) return scale[note.index];
    if (inside[i] !== ANY_FINGER) return inside[i];
    return figure === undefined ? ANY_FINGER : figure.fingers[note.index];
  });
};

/** The span of a line whose notes are on these staff steps (contract 5.1,
 *  step 6); null on a hand without terms on it, and for a line without a note. */
const spanOf = (table: HandTable, steps: ReadonlyArray<number>): Span | null => {
  const terms = table.kind === 'motion' ? table.line : undefined;
  if (terms === undefined || steps.length === 0) return null;
  const towardsFifth = table.side === 'right' ? 1 : -1;
  const low = Math.min(...steps);
  const high = Math.max(...steps);
  return {
    terms,
    towardsFifth,
    thumbEnd: Math.min(towardsFifth * low, towardsFifth * high),
    fifthEnd: Math.max(towardsFifth * low, towardsFifth * high),
  };
};

/** What a note on `step` costs under each finger for where that finger puts the
 *  resting hand against the span (contract 5.1, step 6, `z`): by the keys its
 *  thumb lies beyond the positions the span gives the hand. */
const beyondCosts = (span: Span, step: number): number[] => {
  const { beyond } = span.terms;
  const first = Math.min(span.thumbEnd, span.fifthEnd - FIFTH_COLUMN);
  const last = Math.max(span.thumbEnd, span.fifthEnd - FIFTH_COLUMN);
  const here = span.towardsFifth * step;
  return FINGERS.map((_finger, column) => {
    const thumb = here - column;
    const keys = Math.max(first - thumb, thumb - last, 0);
    return beyond[Math.min(keys, beyond.length - 1)];
  });
};

/** What a move costs for the finger at `column` on the key at `step`, a thumb
 *  or a fifth finger on a key the line goes on beyond on its side (contract
 *  5.1, step 6, `e`): by the colour of that key and of the move's other key. */
const outerCost = (
  span: Span,
  step: number,
  column: number,
  colour: number,
  otherColour: number,
): number => {
  const here = span.towardsFifth * step;
  if (column === THUMB_COLUMN && here > span.thumbEnd) {
    return span.terms.outer[0][2 * colour + otherColour];
  }
  if (column === FIFTH_COLUMN && here < span.fifthEnd) {
    return span.terms.outer[1][2 * colour + otherColour];
  }
  return 0;
};

/** The notes of a line (contract 5.1), or null when a pitch is not the hand's.
 *  `joined`: the line plays on from the phrase before with no rest between. */
function notesOf(
  tables: Tables,
  table: HandTable,
  keyboard: Keyboard,
  line: Line,
  joined: boolean,
): Note[] | null {
  if (line.some((token) => token !== null && table.pitches.indexOf(token) < 0)) return null;
  const places = line
    .map((token, place) => (token === null ? -1 : place))
    .filter((place) => place >= 0);
  const steps = places.map((place) => staffStep(line[place] as string));
  const span = spanOf(table, steps);
  const plain = places.map((place, order): PlainNote => {
    const name = line[place] as string;
    const index = table.pitches.indexOf(name);
    const previousPlace = order === 0 ? -1 : places[order - 1];
    return {
      index,
      name,
      staffStep: steps[order],
      colour: keyboard.colours[index],
      // Every token between two notes is a rest.
      afterRest: place - previousPlace > 1,
      onKey: keyboard.onKey === null ? null : keyboard.onKey[index],
      span,
      beyond: span === null ? null : beyondCosts(span, steps[order]),
    };
  });
  const patterns = patternsOf(tables, table, keyboard, plain, joined);
  return plain.map((note, i) => ({ ...note, pattern: patterns[i] }));
}

/** The pitch of the table the state's key is, or null: no state, or a key the
 *  hand does not have in this key (contract 5.1, step 3). */
const stateIndex = (keyboard: Keyboard, state: HandState | null): number | null => {
  const index = state === null ? -1 : keyboard.midi.indexOf(state.midi);
  return index < 0 ? null : index;
};

/** Whether a line plays on from the phrase before with no rest between
 *  (contract 5.1, step 5): the state is on one of the hand's keys and not
 *  rested, and the line starts with a note. */
const isJoined = (keyboard: Keyboard, line: Line, state: HandState | null): boolean =>
  state !== null &&
  stateIndex(keyboard, state) !== null &&
  !state.rested &&
  line.length > 0 &&
  line[0] !== null;

const leftState = (
  keyboard: Keyboard,
  notes: ReadonlyArray<Note>,
  fingers: ReadonlyArray<Finger>,
  shape: number,
  line: Line,
): HandState => {
  const rested = line[line.length - 1] === null;
  return {
    midi: keyboard.midi[notes[notes.length - 1].index],
    finger: fingers[fingers.length - 1],
    rested,
    shape: rested ? RESTING_SHAPE : shape,
  };
};

/** A phrase in which the hand has no note: it keeps its key and finger, rested. */
const silentResult = (state: HandState | null): HandResult => ({
  ok: true,
  fingers: [],
  need: [],
  arrivals: [],
  gaps: [],
  cost: 0,
  join: state === null ? 'placed' : 'rest',
  state:
    state === null
      ? null
      : { midi: state.midi, finger: state.finger, rested: true, shape: RESTING_SHAPE },
});

// ---------------------------------------------------------------------------
// The need score (contract 5.4)
// ---------------------------------------------------------------------------

const motionCode = (arrival: Arrival | undefined): number =>
  arrival === undefined ? -1 : MOTIONS.indexOf(arrival as Motion);

/** 0 to 100 per note: how much its finger number is needed. `expected`: per
 *  note, the finger a reader expects there, NO_FINGER where none lies over the
 *  key (omitted: nothing is known about what a reader expects, and no note is
 *  settled or unexpected). `featured`: the motion codes the tier features on
 *  the hand. `unclear`: per note, whether the finger a reader would take is
 *  unclear (omitted: on no note). */
export function needScores(
  constants: NeedConstants,
  notes: ReadonlyArray<{ name: string; staffStep: number }>,
  fingers: ReadonlyArray<Finger>,
  arrivals: ReadonlyArray<Arrival>,
  gaps: ReadonlyArray<ReadonlyArray<number | null>>,
  expected?: ReadonlyArray<number>,
  featured: ReadonlyArray<number> = NOT_FEATURED,
  unclear?: ReadonlyArray<boolean>,
): number[] {
  const count = notes.length;

  const firstReached = (arrival: Arrival): number => {
    if (arrival === 'placed') return constants.first.placed;
    if (arrival === 'replaced') return constants.first.replaced;
    if (arrival === 'rest-move') return constants.first.moved;
    if (arrival === 'rest' || arrival === 'none') return constants.first.same;
    return constants.first.motion;
  };

  const laterReached = (arrival: Arrival): number => {
    if (arrival === 'rest-move') return constants.rest_move;
    return motionCode(arrival) >= 0 ? constants.arrival[motionCode(arrival)] : 0;
  };

  const reached = (i: number): number =>
    i === 0 ? firstReached(arrivals[i]) : laterReached(arrivals[i]);

  /** The reader plays the note right unaided: the finger expected there is
   *  good enough, and the note is not reached in a way that is always told. */
  const settled = (i: number): boolean => {
    if (expected === undefined || expected[i] === NO_FINGER) return false;
    const gap = gaps[i][expected[i] - 1];
    return gap !== null && gap <= constants.accept && reached(i) < constants.keep;
  };

  const setup = (i: number): number => {
    const followingCode = motionCode(arrivals[i + 1]);
    return followingCode >= 1 && !settled(i + 1) ? constants.setup[followingCode] : 0;
  };

  const switchback = (i: number): number => {
    const turned =
      i >= 2 &&
      arrivals[i - 1] === 'pivot' &&
      (notes[i].staffStep - notes[i - 1].staffStep) *
        (notes[i - 1].staffStep - notes[i - 2].staffStep) <
        0;
    return turned ? constants.switchback : 0;
  };

  const last = (i: number): number => (i === count - 1 && count > 1 ? constants.last : 0);

  const repeated = (i: number): boolean =>
    i >= 1 &&
    arrivals[i] === 'none' &&
    notes[i].name === notes[i - 1].name &&
    fingers[i] === fingers[i - 1];

  const ambiguous = (i: number): boolean =>
    FINGERS.some((finger, column) => {
      const gap = gaps[i][column];
      return finger !== fingers[i] && gap !== null && gap < constants.ambiguous_gap;
    });

  /** A note under another finger than the one a reader expects, where that one
   *  is not good enough: never lowered. */
  const unexpected = (i: number): number =>
    expected !== undefined && expected[i] !== fingers[i] && !settled(i) ? constants.unexpected : 0;

  const weighed = (i: number): number => {
    const base = settled(i) ? 0 : reached(i);
    const highest = Math.max(base, setup(i), switchback(i), last(i));
    if (highest >= constants.keep) return highest;
    if (repeated(i)) return 0;
    return Math.max(ambiguous(i) ? Math.floor(highest / 2) : highest, unexpected(i));
  };

  /** The first note of the line: told from `need.start` up, however reached. */
  const starts = (i: number): number => (i === 0 ? constants.start : 0);

  /** Whether note `i` exists and is reached by a motion the tier features. */
  const isFeatured = (i: number): boolean => {
    const code = motionCode(arrivals[i]);
    return code >= 0 && featured.indexOf(code) >= 0;
  };

  /** A note of a featured move: the one it lands on, or the one it leaves. */
  const features = (i: number): number =>
    isFeatured(i) || isFeatured(i + 1) ? constants.featured : 0;

  /** A note on which the fingers of the move before leave the reader's finger
   *  unclear. */
  const unclears = (i: number): number =>
    unclear !== undefined && unclear[i] ? constants.unclear : 0;

  return notes.map((_note, i) => Math.max(weighed(i), starts(i), features(i), unclears(i)));
}

/** The lowest need score a display level shows: 'none', 'minimal', 'all', or a
 *  slider percentage (0 to 100). */
export function needThreshold(tables: Tables, level: DisplayLevel | number): number {
  if (typeof level === 'number') return TOP_SCORE - level;
  if (level === 'none') return UNREACHABLE;
  if (level !== 'minimal' && level !== 'all') {
    throw new RangeError(`level must be none, minimal, all or a percentage, not ${String(level)}`);
  }
  return tables.need.levels[level];
}

/** Which numbers of a hand a display level shows (contract, sections 2.9 and
 *  3): none at any level on a hand the tier does not show. */
export function shown(
  tables: Tables,
  tier: string,
  hand: Hand,
  need: ReadonlyArray<number>,
  level: DisplayLevel | number,
): boolean[] {
  const threshold = needThreshold(tables, level);
  const displayed = showsNumbers(tables, tier, hand);
  return need.map((score) => displayed && score >= threshold);
}

// ---------------------------------------------------------------------------
// Position hands (contract 5.2)
// ---------------------------------------------------------------------------

function positionResult(
  tables: Tables,
  table: PositionHandTable,
  keyboard: Keyboard,
  notes: ReadonlyArray<Note>,
  line: Line,
  state: HandState | null,
  featured: ReadonlyArray<number>,
): HandResult {
  const fingers = notes.map((note) => table.fingers[note.index]);
  const held = stateIndex(keyboard, state);
  const continues = state !== null && held !== null && table.fingers[held] === state.finger;
  const rested = state !== null && (state.rested || notes[0].afterRest);
  const join: JoinMode = !continues ? 'placed' : rested ? 'rest' : 'linked';
  const firstArrival: Arrival = join === 'linked' ? 'none' : join;
  const arrivals = notes.map(
    (note, i): Arrival => (i === 0 ? firstArrival : note.afterRest ? 'rest' : 'none'),
  );
  const gaps = fingers.map((mapped) => FINGERS.map((finger) => (finger === mapped ? 0 : null)));
  return {
    ok: true,
    fingers,
    need: needScores(tables.need, notes, fingers, arrivals, gaps, undefined, featured),
    arrivals,
    gaps,
    cost: 0,
    join,
    state: leftState(keyboard, notes, fingers, RESTING_SHAPE, line),
  };
}

// ---------------------------------------------------------------------------
// Motion hands (contract 5.3)
// ---------------------------------------------------------------------------

const moveCell = (finger1: Finger, finger2: Finger): number => 5 * (finger1 - 1) + (finger2 - 1);

/** Whether the finger may be placed on the pitch at all. */
const mayBePlaced = (
  table: MotionHandTable,
  keyboard: Keyboard,
  index: number,
  finger: Finger,
): boolean => table.place[keyboard.keyClass][index][finger - 1] !== NOT_ALLOWED;

/** The shape a hand placed with the finger on the pitch is in (contract 2.3). */
const placedShape = (
  table: MotionHandTable,
  keyboard: Keyboard,
  index: number,
  finger: Finger,
): number => table.place_shape[keyboard.keyClass][index][finger - 1];

/** Whether the hand may rest there: be placed, in a shape that is not open. */
const mayRest = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  index: number,
  finger: Finger,
): boolean =>
  mayBePlaced(table, keyboard, index, finger) &&
  tables.shape.open[placedShape(table, keyboard, index, finger)] === 0;

/** Whether the hand may stop in the seat: end the line there, or be lifted and
 *  placed again. From an open shape it can only play on. */
const mayStop = (tables: Tables, seat: Seat): boolean => tables.shape.open[shapeOf(seat)] === 0;

/** The colour terms of a hand (contract 2.2): its own where it has them, else
 *  the bundle's. */
const colourOfHand = (tables: Tables, table: MotionHandTable): ColourTerms =>
  table.colour ?? tables.colour;

/** What the note itself costs under each finger whatever the move (contract
 *  5.3, `u`): the colour of its key, what the hand's table has for the finger
 *  on that key in this key, where the finger puts the hand against the span of
 *  the line, and another finger than the one a standard pattern gives it. */
const noteCosts = (tables: Tables, table: MotionHandTable, note: Note): number[] => {
  const { unary } = colourOfHand(tables, table);
  const costs = new Array<number>(FINGER_COUNT);
  for (let column = 0; column < FINGER_COUNT; column += 1) {
    const offPattern = note.pattern !== ANY_FINGER && note.pattern !== column + 1;
    costs[column] =
      unary[column][note.colour] +
      (note.onKey === null ? 0 : note.onKey[column]) +
      (note.beyond === null ? 0 : note.beyond[column]) +
      (offPattern ? tables.pattern.cost : 0);
  }
  return costs;
};

/** The hand is placed again on `note` from each finger on the key at
 *  `fromStaffStep`: any finger that may be placed there, priced by how far the
 *  hand moves (`rest_move` during a rest, `replace_move` when there was none),
 *  and during a rest by `rest_hop` where the same finger takes the new key.
 *  `own` is the note's own cost per finger, included. Written out: with
 *  `linkedStep` this fills the tables the inner loop reads. */
const freeStep = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  fromStaffStep: number,
  note: Note,
  own: ReadonlyArray<number>,
  mode: 'rest' | 'replaced',
): Step => {
  const costsByDistance = mode === 'replaced' ? table.replace_move : table.rest_move;
  const farthest = costsByDistance.length - 1;
  const towardsThumb = table.side === 'right' ? -1 : 1;
  const costs = new Array<number>(FINGER_PAIRS);
  const placed = new Array<Seat>(FINGER_COUNT);
  for (let landing = 0; landing < FINGER_COUNT; landing += 1) {
    const finger = FINGERS[landing];
    const allowed = mayBePlaced(table, keyboard, note.index, finger);
    const thumbAfter = note.staffStep + towardsThumb * landing;
    placed[landing] = allowed
      ? seatOf(finger, placedShape(table, keyboard, note.index, finger))
      : NOT_ALLOWED;
    for (let leaving = 0; leaving < FINGER_COUNT; leaving += 1) {
      const apart = Math.abs(thumbAfter - (fromStaffStep + towardsThumb * leaving));
      const hops = mode === 'rest' && leaving === landing && apart > 0;
      costs[FINGER_COUNT * leaving + landing] = allowed
        ? costsByDistance[Math.min(apart, farthest)] +
          (hops ? tables.rest_hop[landing] : 0) +
          own[landing]
        : NOT_ALLOWED;
    }
  }
  return { costs, ops: null, placed };
};

/** What a linked move onto `note` from the key at `fromStaffStep` costs for a
 *  thumb or a fifth finger on a key the line goes on beyond (contract 5.3,
 *  `E`), in two parts: by the finger that leaves and by the finger that lands,
 *  each at `finger - 1`. Nothing on a hand without terms on the line's span. */
const outerCosts = (
  note: Note,
  fromStaffStep: number,
  fromColour: number,
): { leaving: ReadonlyArray<number>; landing: ReadonlyArray<number> } => {
  const { span } = note;
  if (span === null) return { leaving: NO_COST, landing: NO_COST };
  return {
    leaving: FINGERS.map((_finger, column) =>
      outerCost(span, fromStaffStep, column, fromColour, note.colour),
    ),
    landing: FINGERS.map((_finger, column) =>
      outerCost(span, note.staffStep, column, note.colour, fromColour),
    ),
  };
};

/** The linked moves from each finger on one key onto `note`, the note's own
 *  cost, what the change of shape costs on the colours of the two keys and
 *  what an outer finger costs on a key the line goes on beyond included, and
 *  what the move costs from the shape the hand is in left out: that one depends
 *  on the seat. Across a rest the hand may be held as it is for the motions
 *  `across_rest` allows, onto a finger the hand may rest with. */
const linkedStep = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  fromIndex: number,
  fromStaffStep: number,
  note: Note,
  own: ReadonlyArray<number>,
  where: Where,
): Step => {
  const bases = table.linked[keyboard.keyClass][fromIndex][note.index];
  const motions = table.motion[fromIndex][note.index];
  const ops = table.shape_op[keyboard.keyClass][fromIndex][note.index];
  const colours = 2 * keyboard.colours[fromIndex] + note.colour;
  const acrossRest = where === 'across-rest';
  const insideLine = where === 'within';
  const { pair } = colourOfHand(tables, table);
  const shapeColour = tables.shape.colour;
  const outer = outerCosts(note, fromStaffStep, keyboard.colours[fromIndex]);
  const costs = new Array<number>(FINGER_PAIRS);
  for (let landing = 0; landing < FINGER_COUNT; landing += 1) {
    const lands = !acrossRest || mayRest(tables, table, keyboard, note.index, FINGERS[landing]);
    for (let leaving = 0; leaving < FINGER_COUNT; leaving += 1) {
      const cell = FINGER_COUNT * leaving + landing;
      const base = lands ? bases[cell] : NOT_ALLOWED;
      const motion = motions[cell];
      const held = acrossRest && base !== NOT_ALLOWED ? tables.across_rest[motion] : 0;
      costs[cell] =
        base !== NOT_ALLOWED && held !== NOT_ALLOWED
          ? base +
            held +
            shapeColour[ops[cell]][colours] +
            pair[motion][leaving][landing][colours] +
            outer.leaving[leaving] +
            outer.landing[landing] +
            (insideLine ? tables.within[motion][landing] : 0) +
            own[landing]
          : NOT_ALLOWED;
    }
  }
  return { costs, ops, placed: NOWHERE_PLACED };
};

/** The steps onto `note` from the key at `fromIndex` (staff step
 *  `fromStaffStep`). `where` is 'joining' for the move from the hand state into
 *  the first note, 'within' for a move inside the line. Across a rest inside
 *  the line the hand is placed again or held as it is. */
const stepsOnto = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  mode: 'linked' | 'rest' | 'replaced',
  fromIndex: number,
  fromStaffStep: number,
  note: Note,
  where: 'joining' | 'within',
): Step[] => {
  const own = noteCosts(tables, table, note);
  if (mode === 'linked') {
    return [linkedStep(tables, table, keyboard, fromIndex, fromStaffStep, note, own, where)];
  }
  const free = freeStep(tables, table, keyboard, fromStaffStep, note, own, mode);
  return where === 'within'
    ? [
        free,
        linkedStep(tables, table, keyboard, fromIndex, fromStaffStep, note, own, 'across-rest'),
      ]
    : [free];
};

/**
 * Every allowed move from the seats of one note over the steps onto the next,
 * and the layer of the next note they reach. This is the inner loop of the
 * search, so it is written out on flat arrays: a layer and its moves are
 * parallel arrays of numbers, and a move names its two seats by their places
 * in the two layers.
 *
 * `places` is the search's scratch: one entry per seat, every entry NOT_REACHED
 * when this is called and again when it returns. In between it holds the place
 * of each seat reached so far.
 *
 * A hand in an open shape is not placed again; a linked or held move is not
 * made from a shape its change of shape cannot be made from.
 */
const advanced = (
  tables: Tables,
  steps: ReadonlyArray<Step>,
  from: Layer,
  places: Int32Array,
): { layer: Layer; moves: Moves } => {
  const { next, cost: shapeCost, open } = tables.shape;
  const most = from.seats.length * steps.length * FINGER_COUNT;
  const starts = new Array<number>(from.seats.length + 1);
  const targets = new Array<number>(most);
  const moveCosts = new Array<number>(most);
  const seats: Seat[] = [];
  const reached: number[] = [];
  let count = 0;
  for (let place = 0; place < from.seats.length; place += 1) {
    const leaving = from.seats[place] % FINGER_COUNT;
    const fromShape = (from.seats[place] - leaving) / FINGER_COUNT;
    const before = from.costs[place];
    starts[place] = count;
    for (let taken = 0; taken < steps.length; taken += 1) {
      const { costs, ops, placed } = steps[taken];
      if (ops !== null || open[fromShape] === 0) {
        for (let landing = 0; landing < FINGER_COUNT; landing += 1) {
          const cell = FINGER_COUNT * leaving + landing;
          // The seat the move lands in and what it costs; no seat: not a move.
          let seat = NOT_REACHED;
          let cost = costs[cell];
          if (cost !== NOT_ALLOWED) {
            if (ops === null) {
              seat = placed[landing];
            } else if (shapeCost[ops[cell]][fromShape] !== NOT_ALLOWED) {
              seat = FINGER_COUNT * next[ops[cell]][fromShape] + landing;
              cost += shapeCost[ops[cell]][fromShape];
            }
          }
          if (seat !== NOT_REACHED) {
            const known = places[seat];
            if (known === NOT_REACHED) {
              places[seat] = seats.length;
              targets[count] = seats.length;
              seats.push(seat);
              reached.push(before + cost);
            } else {
              targets[count] = known;
              if (before + cost < reached[known]) reached[known] = before + cost;
            }
            moveCosts[count] = cost;
            count += 1;
          }
        }
      }
    }
  }
  starts[from.seats.length] = count;
  for (let place = 0; place < seats.length; place += 1) places[seats[place]] = NOT_REACHED;
  return { layer: { seats, costs: reached }, moves: { starts, targets, costs: moveCosts } };
};

/** For each seat of the earlier note, the cheapest way on to the end of the
 *  line; UNREACHABLE where the line cannot be finished from there. `later` is
 *  the same for the note the moves lead to. */
const relaxedBack = (later: ReadonlyArray<number>, moves: Moves): number[] => {
  const { starts, targets, costs } = moves;
  const earlier = new Array<number>(starts.length - 1);
  for (let place = 0; place < earlier.length; place += 1) {
    const end = starts[place + 1];
    let cheapest = UNREACHABLE;
    for (let move = starts[place]; move < end; move += 1) {
      const onward = costs[move] + later[targets[move]];
      if (onward < cheapest) cheapest = onward;
    }
    earlier[place] = cheapest;
  }
  return earlier;
};

const firstEntry = (keyboard: Keyboard, state: HandState | null, first: Note): Entry => {
  const held = stateIndex(keyboard, state);
  if (state === null || held === null) return { mode: 'placed' };
  return { mode: state.rested || first.afterRest ? 'rest' : 'linked', held, state };
};

/** The seats the first note can be entered in and what each costs, the note's
 *  own cost included. `places` is the search's scratch (see `advanced`). */
const entryLayer = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  entry: Entry,
  first: Note,
  places: Int32Array,
): Layer => {
  if (entry.mode === 'placed') {
    const own = noteCosts(tables, table, first);
    const fingers = FINGERS.filter((finger) => mayBePlaced(table, keyboard, first.index, finger));
    return {
      seats: fingers.map((finger) =>
        seatOf(finger, placedShape(table, keyboard, first.index, finger)),
      ),
      costs: fingers.map(
        (finger) => table.place[keyboard.keyClass][first.index][finger - 1] + own[finger - 1],
      ),
    };
  }
  const shape = entry.state.shape ?? RESTING_SHAPE;
  const known = entry.mode === 'linked' && shape < tables.shape.count;
  const seat = seatOf(entry.state.finger, known ? shape : RESTING_SHAPE);
  const steps = stepsOnto(
    tables,
    table,
    keyboard,
    entry.mode,
    entry.held,
    staffStep(table.pitches[entry.held]),
    first,
    'joining',
  );
  return advanced(tables, steps, { seats: [seat], costs: [0] }, places).layer;
};

/** The layers and the moves of a line from the layer of its first note on. */
const throughLine = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  first: Layer,
  notes: ReadonlyArray<Note>,
  places: Int32Array,
): { forward: Layer[]; moves: Moves[] } => {
  const forward: Layer[] = [first];
  const moves: Moves[] = [];
  for (let i = 1; i < notes.length; i += 1) {
    const steps = stepsOnto(
      tables,
      table,
      keyboard,
      notes[i].afterRest ? 'rest' : 'linked',
      notes[i - 1].index,
      notes[i - 1].staffStep,
      notes[i],
      'within',
    );
    const onward = advanced(tables, steps, forward[i - 1], places);
    forward.push(onward.layer);
    moves.push(onward.moves);
  }
  return { forward, moves };
};

const scratchOf = (tables: Tables): Int32Array =>
  new Int32Array(FINGER_COUNT * tables.shape.count).fill(NOT_REACHED);

/** The search as far as the last note (contract 5.3, step 1). */
function reach(
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  entry: Entry,
  notes: ReadonlyArray<Note>,
): Reached {
  const places = scratchOf(tables);
  const first = entryLayer(tables, table, keyboard, entry, notes[0], places);
  return { entry, ...throughLine(tables, table, keyboard, first, notes, places) };
}

/** For each seat of the last note, what ending the line there costs on top:
 *  nothing in a seat the line may end in, UNREACHABLE in the others. */
const endsOf = (tables: Tables, last: Layer): number[] =>
  last.seats.map((seat) => (mayStop(tables, seat) ? 0 : UNREACHABLE));

/** The search finished from what each seat of the last note costs on top
 *  (contract 5.3, steps 3 and 4): the backward pass and the best total. */
function finish(reached: Reached, finished: ReadonlyArray<number>): Search {
  const { entry, forward, moves } = reached;
  const backward = new Array<ReadonlyArray<number>>(forward.length);
  backward[forward.length - 1] = finished;
  for (let i = forward.length - 2; i >= 0; i -= 1) {
    backward[i] = relaxedBack(backward[i + 1], moves[i]);
  }
  const best = forward[forward.length - 1].costs.reduce(
    (lowest, cost, place) => Math.min(lowest, cost + finished[place]),
    UNREACHABLE,
  );
  return { entry, forward, backward, moves, best };
}

function search(
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  entry: Entry,
  notes: ReadonlyArray<Note>,
): Search {
  const reached = reach(tables, table, keyboard, entry, notes);
  return finish(reached, endsOf(tables, reached.forward[reached.forward.length - 1]));
}

/** Whether the line can be finished: some seat it may end in is reached. */
const isFound = (found: Search): boolean => found.best < UNREACHABLE;

/**
 * What playing the line that follows costs from each seat this line can end in
 * (contract 5.6), by the place of the seat in the last note's layer: the best
 * fingering of that line entered from the state the seat leaves, across a rest
 * or linked, with no fallback. UNREACHABLE where it cannot be played on from
 * there, and in a seat this line may not end in. Null when no seat has a cost:
 * the line that follows has no note, has a pitch that is not the hand's, or can
 * be played on from no ending.
 *
 * It is one search over the two lines laid end to end: the seats of the last
 * note are the layer the line that follows is entered from, all at once, and
 * its backward pass says what each of them has ahead.
 */
function onwardCosts(
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  notes: ReadonlyArray<Note>,
  line: Line,
  following: Line,
  last: Layer,
): number[] | null {
  const rested = line[line.length - 1] === null;
  const mode = rested || following[0] === null ? 'rest' : 'linked';
  // The line that follows is joined exactly when it is entered linked.
  const ahead = notesOf(tables, table, keyboard, following, mode === 'linked');
  if (ahead === null || ahead.length === 0) return null;
  // The state a seat leaves names its key by what sounds there, so the line
  // that follows is entered from the hand's first pitch on that key.
  const held = keyboard.midi.indexOf(keyboard.midi[notes[notes.length - 1].index]);
  const places = scratchOf(tables);
  // Across a rest the hand is placed again, whatever shape the seat was in.
  const leaving: Layer = { seats: last.seats, costs: last.seats.map(() => 0) };
  const steps = stepsOnto(
    tables,
    table,
    keyboard,
    mode,
    held,
    staffStep(table.pitches[held]),
    ahead[0],
    'joining',
  );
  const entered = advanced(tables, steps, leaving, places);
  const onward = throughLine(tables, table, keyboard, entered.layer, ahead, places);
  const beyond = finish(
    { entry: { mode: 'placed' }, ...onward },
    endsOf(tables, onward.forward[onward.forward.length - 1]),
  );
  const costs = relaxedBack(beyond.backward[0], entered.moves).map((cost, place) =>
    mayStop(tables, last.seats[place]) ? cost : UNREACHABLE,
  );
  return costs.some((cost) => cost < UNREACHABLE) ? costs : null;
}

/** Whether `fingers` comes before `other`: the lower finger at the first note
 *  where they differ. */
const comesBefore = (fingers: ReadonlyArray<Finger>, other: ReadonlyArray<Finger>): boolean => {
  const differs = fingers.findIndex((finger, i) => finger !== other[i]);
  return differs >= 0 && fingers[differs] < other[differs];
};

/** Whether `trail` is the one a seat keeps of two it is reached with: the
 *  smaller finger list, and of two with the same list the one that came from
 *  the lower shape. */
const isKept = (trail: Trail, other: Trail): boolean =>
  comesBefore(trail.fingers, other.fingers) ||
  (!comesBefore(other.fingers, trail.fingers) && trail.cameFrom < other.cameFrom);

/**
 * The fingers, and the shape the hand is in on every note, of the fingering
 * that reaches the best total and whose costs fall latest (contract 5.3, step
 * 5): note by note the lower step cost wins at the first difference, and among
 * fingerings with the same step costs the lowest finger at the first note where
 * they differ. A seat is reached with the smallest finger list and, where
 * several seats before it carry that list, from the one in the lowest shape; at
 * the end the smallest finger list wins, then the lowest shape.
 */
function pathOf(found: Search): { fingers: Finger[]; shapes: number[]; place: number } {
  const { best } = found;
  const first = found.forward[0];
  const entered = first.seats
    .map((_seat, place) => place)
    .filter((place) => first.costs[place] + found.backward[0][place] === best);
  const cheapestEntry = Math.min(...entered.map((place) => first.costs[place]));
  /** The fingering each candidate seat is reached with, by the seat's place in
   *  the layer of the note the trails have got to. */
  const start = new Map<number, Trail>(
    entered
      .filter((place) => first.costs[place] === cheapestEntry)
      .map((place): [number, Trail] => [
        place,
        {
          fingers: [fingerOf(first.seats[place])],
          shapes: [shapeOf(first.seats[place])],
          cameFrom: RESTING_SHAPE,
        },
      ]),
  );

  /** The trails that go on over the cheapest optimal moves into note `i`. */
  const extend = (trails: ReadonlyMap<number, Trail>, moves: Moves, i: number) => {
    const before = found.forward[i - 1].costs;
    const after = found.backward[i];
    const optimal: Array<{ move: number; source: number; trail: Trail }> = [];
    trails.forEach((trail, source) => {
      for (let move = moves.starts[source]; move < moves.starts[source + 1]; move += 1) {
        if (before[source] + moves.costs[move] + after[moves.targets[move]] === best) {
          optimal.push({ move, source, trail });
        }
      }
    });
    const least = optimal.reduce(
      (lowest, { move }) => Math.min(lowest, moves.costs[move]),
      UNREACHABLE,
    );
    const kept = new Map<number, Trail>();
    optimal
      .filter(({ move }) => moves.costs[move] === least)
      .forEach(({ move, source, trail }) => {
        const place = moves.targets[move];
        const seat = found.forward[i].seats[place];
        const longer: Trail = {
          fingers: trail.fingers.concat([fingerOf(seat)]),
          shapes: trail.shapes.concat([shapeOf(seat)]),
          cameFrom: shapeOf(found.forward[i - 1].seats[source]),
        };
        const known = kept.get(place);
        if (known === undefined || isKept(longer, known)) kept.set(place, longer);
      });
    return kept;
  };

  const trails = found.moves.reduce<ReadonlyMap<number, Trail>>(
    (kept, moves, i) => extend(kept, moves, i + 1),
    start,
  );
  const lastSeats = found.forward[found.forward.length - 1].seats;
  // Ties between seats go to the lower shape: seats are visited in rising order.
  const [place, chosen] = Array.from(trails.entries())
    .sort(([one], [other]) => lastSeats[one] - lastSeats[other])
    .reduce((kept, candidate) =>
      comesBefore(candidate[1].fingers, kept[1].fingers) ? candidate : kept,
    );
  return { fingers: chosen.fingers.slice(), shapes: chosen.shapes.slice(), place };
}

const gapsOf = (found: Search): Array<Array<number | null>> =>
  found.forward.map((layer, i) => {
    const onward = found.backward[i];
    const through: number[] = [UNREACHABLE, UNREACHABLE, UNREACHABLE, UNREACHABLE, UNREACHABLE];
    for (let place = 0; place < layer.seats.length; place += 1) {
      const column = layer.seats[place] % FINGER_COUNT;
      const whole = layer.costs[place] + onward[place];
      if (whole < through[column]) through[column] = whole;
    }
    return through.map((cost) => (cost === UNREACHABLE ? null : cost - found.best));
  });

const freeArrival = (
  table: MotionHandTable,
  fromIndex: number,
  fromFinger: Finger,
  note: Note,
  finger: Finger,
): Arrival =>
  thumbStep(table.side, note.staffStep, finger) ===
  position(table.side, table.pitches[fromIndex], fromFinger)
    ? 'rest'
    : 'rest-move';

const motionName = (
  table: MotionHandTable,
  fromIndex: number,
  fromFinger: Finger,
  note: Note,
  finger: Finger,
): Arrival => MOTIONS[table.motion[fromIndex][note.index][moveCell(fromFinger, finger)]];

function arrivalsOf(
  table: MotionHandTable,
  entry: Entry,
  notes: ReadonlyArray<Note>,
  fingers: ReadonlyArray<Finger>,
): Arrival[] {
  const first = (): Arrival => {
    if (entry.mode === 'placed' || entry.mode === 'replaced') return entry.mode;
    return entry.mode === 'rest'
      ? freeArrival(table, entry.held, entry.state.finger, notes[0], fingers[0])
      : motionName(table, entry.held, entry.state.finger, notes[0], fingers[0]);
  };
  const later = (i: number): Arrival =>
    notes[i].afterRest
      ? freeArrival(table, notes[i - 1].index, fingers[i - 1], notes[i], fingers[i])
      : motionName(table, notes[i - 1].index, fingers[i - 1], notes[i], fingers[i]);
  return notes.map((_note, i) => (i === 0 ? first() : later(i)));
}

/** The finger that lies over the note's key in a hand with `finger` on the key
 *  at `fromStaffStep` (contract 5.4): as `shape` holds the fingers; where none
 *  lies there so and `need.at_rest` is set, as the hand at rest holds them;
 *  NO_FINGER when none does. `atRest`: the hand is read at rest whatever its
 *  shape. */
const lyingFinger = (
  tables: Tables,
  table: MotionHandTable,
  fromStaffStep: number,
  finger: Finger,
  shape: number,
  note: Note,
  atRest: boolean,
): number => {
  const towardsFifth = table.side === 'right' ? 1 : -1;
  const keys = (note.staffStep - fromStaffStep) * towardsFifth;
  const lying = (gaps: ReadonlyArray<number>): number => {
    /** How many keys each finger lies from the thumb, towards the fifth finger. */
    const fromThumb = accumulate(gaps, (sum: number, gap) => sum + gap, 0);
    return (
      FINGERS.find((other) => fromThumb[other - 1] - fromThumb[finger - 1] === keys) ?? NO_FINGER
    );
  };
  const resting = tables.shape.gaps[RESTING_SHAPE];
  if (atRest) return lying(resting);
  const held = lying(tables.shape.gaps[shape]);
  return held !== NO_FINGER || tables.need.at_rest !== 1 ? held : lying(resting);
};

/** Per note, the finger a reader expects on it (contract 5.4): the one that
 *  lies over its key in the hand as the note before left it; for the first
 *  note, as the phrase before left it (NO_FINGER when the hand is placed).
 *  `atRest`: the hand is read at rest around the finger that played. */
const expectedOf = (
  tables: Tables,
  table: MotionHandTable,
  entry: Entry,
  notes: ReadonlyArray<Note>,
  fingers: ReadonlyArray<Finger>,
  shapes: ReadonlyArray<number>,
  atRest: boolean,
): number[] => {
  const first = (): number => {
    if (entry.mode === 'placed' || entry.mode === 'replaced') return NO_FINGER;
    const shape = entry.state.shape ?? RESTING_SHAPE;
    const known = entry.mode === 'linked' && shape < tables.shape.count;
    return lyingFinger(
      tables,
      table,
      staffStep(table.pitches[entry.held]),
      entry.state.finger,
      known ? shape : RESTING_SHAPE,
      notes[0],
      atRest,
    );
  };
  return notes.map((note, i) =>
    i === 0
      ? first()
      : lyingFinger(
          tables,
          table,
          notes[i - 1].staffStep,
          fingers[i - 1],
          shapes[i - 1],
          note,
          atRest,
        ),
  );
};

/** Per note, whether the finger a reader would take is unclear (contract 5.4,
 *  step 13). The note before was reached by a stretch or a squeeze, so the hand
 *  holds two fingers apart from where it has them at rest: the finger just
 *  played and the one before it, each on its key. A reader's finger is the one
 *  the hand at rest around the finger just played has over the note. Away from
 *  the held finger that is the hand as it is. Towards it, it is only where the
 *  note lies between the two keys and that finger between the two fingers. A
 *  note played again with the same finger leaves the hand as it was. */
const unclearOf = (
  tables: Tables,
  table: MotionHandTable,
  entry: Entry,
  notes: ReadonlyArray<Note>,
  fingers: ReadonlyArray<Finger>,
  arrivals: ReadonlyArray<Arrival>,
): boolean[] => {
  const repeats = (i: number): boolean =>
    i >= 1 &&
    arrivals[i] === 'none' &&
    notes[i].name === notes[i - 1].name &&
    fingers[i] === fingers[i - 1];

  /** The last note up to note `i` that is not a note played again. */
  const landedBy = (i: number): number => (repeats(i) ? landedBy(i - 1) : i);

  /** The staff step and the finger the hand held before note `i`. */
  const heldBefore = (i: number): { step: number; finger: number } | null => {
    if (i > 0) return { step: notes[i - 1].staffStep, finger: fingers[i - 1] };
    return entry.mode === 'placed'
      ? null
      : { step: staffStep(table.pitches[entry.held]), finger: entry.state.finger };
  };

  const unclear = (i: number): boolean => {
    if (i === 0) return false;
    const landed = landedBy(i - 1);
    const held = heldBefore(landed);
    if (held === null || HOLDS_TWO_FINGERS.indexOf(arrivals[landed]) < 0) return false;
    const played = notes[landed].staffStep;
    const here = notes[i].staffStep;
    const atRest = lyingFinger(
      tables,
      table,
      played,
      fingers[landed],
      RESTING_SHAPE,
      notes[i],
      true,
    );
    const towardsTheHeld = (here - played) * (held.step - played) > 0;
    const betweenTheKeys = (here - played) * (held.step - here) > 0;
    const betweenTheFingers = (atRest - fingers[landed]) * (held.finger - atRest) > 0;
    return atRest !== NO_FINGER && towardsTheHeld && !(betweenTheKeys && betweenTheFingers);
  };
  return notes.map((_note, i) => unclear(i));
};

/** The search in the first note's mode, falling back from linked to replaced.
 *  With the line that follows, each search that finds an ending is finished
 *  again from what playing on costs, where any ending can be played on from. */
function bestSearch(
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  state: HandState | null,
  notes: ReadonlyArray<Note>,
  line: Line,
  following: Line | null,
): Search {
  const searched = (entry: Entry): Search => {
    const reached = reach(tables, table, keyboard, entry, notes);
    const last = reached.forward[reached.forward.length - 1];
    const alone = finish(reached, endsOf(tables, last));
    if (following === null || !isFound(alone)) return alone;
    const onward = onwardCosts(tables, table, keyboard, notes, line, following, last);
    return onward === null ? alone : finish(reached, onward);
  };
  const entry = firstEntry(keyboard, state, notes[0]);
  const found = searched(entry);
  if (isFound(found) || entry.mode !== 'linked') return found;
  return searched({ ...entry, mode: 'replaced' });
}

function motionResult(
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  notes: ReadonlyArray<Note>,
  line: Line,
  state: HandState | null,
  following: Line | null,
  display: HandDisplay | null,
): HandResult {
  const found = bestSearch(tables, table, keyboard, state, notes, line, following);
  if (!isFound(found)) return notOk();
  const { fingers, shapes, place } = pathOf(found);
  const gaps = gapsOf(found);
  const arrivals = arrivalsOf(table, found.entry, notes, fingers);
  const atRest = display?.neutral === true;
  const expected = expectedOf(tables, table, found.entry, notes, fingers, shapes, atRest);
  return {
    ok: true,
    fingers,
    need: needScores(
      tables.need,
      notes,
      fingers,
      arrivals,
      gaps,
      expected,
      display?.featured ?? NOT_FEATURED,
      display?.unclear === true
        ? unclearOf(tables, table, found.entry, notes, fingers, arrivals)
        : undefined,
    ),
    arrivals,
    gaps,
    // The fingering's own cost: the best total less what follows its last seat.
    cost: found.best - found.backward[found.backward.length - 1][place],
    join: found.entry.mode,
    state: leftState(keyboard, notes, fingers, shapes[shapes.length - 1], line),
  };
}

// ---------------------------------------------------------------------------
// The functions of the contract (section 3)
// ---------------------------------------------------------------------------

/** The fingering of one hand of one phrase, its need scores and the state it
 *  leaves. With `input.next`, the same hand's line of the phrase that comes
 *  next, the fingering is chosen with what playing on into it costs. */
export function fingerHand(tables: Tables, tier: string, hand: Hand, input: HandInput): HandResult {
  const line = checkedLine(input.line);
  const key = checkedKey(input.key);
  const state = checkedState(input.state);
  const following =
    input.next === undefined || input.next === null ? null : checkedLine(input.next);
  const table = handTable(tables, tier, hand);
  if (table === null) return notOk();
  const keyboard = keyboardOf(table, key);
  const notes = notesOf(tables, table, keyboard, line, isJoined(keyboard, line, state));
  if (notes === null) return notOk();
  if (notes.length === 0) return silentResult(state);
  const display = handDisplay(tables, tier, hand);
  return table.kind === 'position'
    ? positionResult(tables, table, keyboard, notes, line, state, display?.featured ?? NOT_FEATURED)
    : motionResult(tables, table, keyboard, notes, line, state, following, display);
}

/** `fingerHand` per hand of a phrase; the hands do not interact. */
export function fingerPhrase(
  tables: Tables,
  tier: string,
  hands: Partial<Record<Hand, HandInput>>,
): Partial<Record<Hand, HandResult>> {
  return HANDS.reduce<Partial<Record<Hand, HandResult>>>((results, hand) => {
    const input = hands[hand];
    return input === undefined
      ? results
      : { ...results, [hand]: fingerHand(tables, tier, hand, input) };
  }, {});
}

/** The staff steps the tier lets the hand's line move from one note to the
 *  next, or null when it has no such rule (contract 2.7). */
const stepsAllowed = (tables: Tables, tier: string, hand: Hand): ReadonlyArray<number> | null =>
  ownValue(tables.tiers, tier)?.steps?.[hand] ?? null;

/** Whether the step from the hand's last note to the line's first is one the
 *  tier does not allow (contract 5.5): judged from note to note, a rest between
 *  them or not. */
const breaksThePitchRule = (
  tables: Tables,
  tier: string,
  hand: Hand,
  table: HandTable,
  held: number | null,
  notes: ReadonlyArray<Note>,
): boolean => {
  const allowed = stepsAllowed(tables, tier, hand);
  if (allowed === null || held === null || notes.length === 0) return false;
  return allowed.indexOf(Math.abs(notes[0].staffStep - staffStep(table.pitches[held]))) < 0;
};

/** Whether the line, sounding in `key`, can follow the state (contract 5.5). */
export function joinHand(
  tables: Tables,
  tier: string,
  hand: Hand,
  state: HandState | null,
  line: Line,
  key?: Key,
): Join {
  const tokens = checkedLine(line);
  const sounding = checkedKey(key);
  const start = checkedState(state);
  const table = handTable(tables, tier, hand);
  if (table === null) return NO_JOIN;
  const keyboard = keyboardOf(table, sounding);
  const notes = notesOf(tables, table, keyboard, tokens, isJoined(keyboard, tokens, start));
  if (notes === null) return NO_JOIN;
  const held = stateIndex(keyboard, start);
  if (breaksThePitchRule(tables, tier, hand, table, held, notes)) return NO_JOIN;
  if (table.kind === 'position' || notes.length === 0 || start === null || held === null) {
    return FREE_JOIN;
  }
  if (start.rested || notes[0].afterRest) return FREE_JOIN;
  const linked = search(tables, table, keyboard, { mode: 'linked', held, state: start }, notes);
  if (!isFound(linked)) return NO_JOIN;
  const placed = search(tables, table, keyboard, { mode: 'placed' }, notes);
  const neededAnyway = isFound(placed) ? Math.floor(placed.best / tables.detached_unit) : 0;
  return {
    compatible: true,
    hops: Math.max(0, Math.floor(linked.best / tables.detached_unit) - neededAnyway),
  };
}

/** Compatible when every hand is; the hops add up. */
export function joinPhrase(
  tables: Tables,
  tier: string,
  states: Partial<Record<Hand, HandState | null>>,
  lines: Partial<Record<Hand, Line>>,
  key?: Key,
): Join {
  return HANDS.reduce<Join>((total, hand) => {
    const line = lines[hand];
    if (line === undefined) return total;
    const join = joinHand(tables, tier, hand, states[hand] ?? null, line, key);
    return { compatible: total.compatible && join.compatible, hops: total.hops + join.hops };
  }, FREE_JOIN);
}

/** The join check of a line in one key from every possible state, as one
 *  character per (pitch, finger): 'n' not compatible whether or not a rest came
 *  before (the tier's rule on steps), 'x' not compatible when no rest did, else
 *  the hops (capped at 9). Empty: free from any state. A single 'x': the line
 *  holds a pitch the hand does not have, so it is compatible from no state, a
 *  missing one included (contract 6.2). */
export function entryRecord(
  tables: Tables,
  tier: string,
  hand: Hand,
  line: Line,
  key?: Key,
): string {
  const tokens = checkedLine(line);
  const sounding = checkedKey(key);
  const table = handTable(tables, tier, hand);
  if (table === null) throw new RangeError(`${tier} has no ${hand} table`);
  const keyboard = keyboardOf(table, sounding);
  if (notesOf(tables, table, keyboard, tokens, false) === null) return NEVER_COMPATIBLE;
  const silent = tokens.every((token) => token === null);
  const ruled = stepsAllowed(tables, tier, hand) !== null && !silent;
  const alwaysFree = !ruled && (table.kind === 'position' || silent || tokens[0] === null);
  if (alwaysFree) return '';
  const mark = (midi: number, finger: Finger): string => {
    const rested = joinHand(tables, tier, hand, { midi, finger, rested: true }, tokens, sounding);
    if (!rested.compatible) return PITCH_RULE;
    const join = joinHand(tables, tier, hand, { midi, finger, rested: false }, tokens, sounding);
    return join.compatible ? `${Math.min(join.hops, 9)}` : NEVER_COMPATIBLE;
  };
  return keyboard.midi.map((midi) => FINGERS.map((finger) => mark(midi, finger)).join('')).join('');
}
